import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  alpha,
  useTheme,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
} from "@mui/material";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { fetchKForm, setYear, setMonth } from "../../../store/slices/kFormSlice";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year, label: year.toString() };
});

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "0.00";
  return parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const splitAmount = (amount) => {
  const value = amount || amount === 0 ? parseFloat(amount) : 0;
  const fixed = value.toFixed(2);
  const [rupees, cents] = fixed.split(".");
  const rupeesWithCommas = parseInt(rupees, 10).toLocaleString("en-US");
  return { rupees: rupeesWithCommas, cents };
};

const PRIMARY = "#1B5C20";
const PRIMARY_DARK = "#0D3B12";

const KFormPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const printRef = useRef();

  const {
    data,
    loading,
    error,
    selectedYear,
    selectedMonth,
    employerName,
    employerAddress,
    epfRegistrationNumber,
  } = useSelector((state) => state.kForm);

  const [year, setYearLocal] = useState(selectedYear || new Date().getFullYear());
  const [month, setMonthLocal] = useState(selectedMonth || new Date().getMonth() + 1);
  const [printLoading, setPrintLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchKForm({ year: selectedYear || year, month: selectedMonth || month }));
  }, [dispatch, selectedYear, selectedMonth]);

  const handleFetch = () => {
    dispatch(setYear(year));
    dispatch(setMonth(month));
    dispatch(fetchKForm({ year, month }));
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `ETF_R4_${year}_${String(month).padStart(2, "0")}`,
    onBeforePrint: () => {
      setPrintLoading(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setPrintLoading(false);
      toast.success("Print initiated successfully");
    },
    onPrintError: () => {
      setPrintLoading(false);
      toast.error("Failed to print. Please try again.");
    },
  });

  const handleExportPDF = async () => {
    if (data.length === 0) {
      toast.warning("No data to export. Please generate the report first.");
      return;
    }

    try {
      setPdfLoading(true);
      toast.info("Generating PDF...", { autoClose: 1500 });

      const element = printRef.current;
      if (!element) {
        throw new Error("Nothing to export");
      }

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: element.offsetWidth,
        height: element.offsetHeight,
        windowWidth: element.offsetWidth,
        windowHeight: element.offsetHeight,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;

      while (heightLeft > 0) {
        position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      const fileName = `ETF_R4_${year}_${String(month).padStart(2, "0")}.pdf`;
      pdf.save(fileName);

      toast.success("PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF Export Error:", err);
      toast.error(`Failed to generate PDF: ${err.message || "Please try again."}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const headers = [
        "Employee's Name",
        "National Identity Card No.",
        "Member No.",
        "Contribution",
      ];

      const rows = data.map((item) => [
        `"${item.EmployeeName || ""}"`,
        `"${item.NationalIdentityCardNo || ""}"`,
        `"${item.MemberNo || ""}"`,
        formatCurrency(item.Contribution),
      ]);

      const totalContribution = data.reduce(
        (sum, item) => sum + parseFloat(item.Contribution || 0),
        0
      );

      rows.push(['"TOTAL"', "", "", formatCurrency(totalContribution)]);

      const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ETF_R4_${year}_${String(month).padStart(2, "0")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success("CSV downloaded successfully!");
    } catch (err) {
      console.error("CSV Export Error:", err);
      toast.error("Failed to export CSV");
    }
  };

  const getMonthShort = (monthValue) => {
    const m = MONTHS.find((mm) => mm.value === monthValue);
    return m ? m.label.slice(0, 3) : "";
  };

  const totalContribution = data.reduce(
    (sum, item) => sum + parseFloat(item.Contribution || 0),
    0
  );
  const totalSplit = splitAmount(totalContribution);
  const noOfEmployees = data.length;

  const cellBorder = "1px solid #000";

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: "#F8FAF8", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <HomeIcon fontSize="small" />
            Home
          </Link>
          <Link underline="hover" color="inherit" href="/payroll" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Payroll
          </Link>
          <Link underline="hover" color="inherit" href="/payroll/reports" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Reports
          </Link>
          <Typography color="text.primary" fontWeight={600}>
            ETF R4 Form
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color={PRIMARY} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceIcon sx={{ fontSize: 32 }} />
              ETF Advice of Remittance (R4)
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Employees' Trust Fund Board - Advice of Remittance Form
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/payroll/reports")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              borderColor: PRIMARY,
              color: PRIMARY,
              "&:hover": { borderColor: PRIMARY_DARK, bgcolor: alpha(PRIMARY, 0.05) },
            }}
          >
            Back to Reports
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: "#FFFFFF" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth label="Year" value={year} onChange={(e) => setYearLocal(parseInt(e.target.value))} size="small">
              {YEARS.map((y) => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth label="Month" value={month} onChange={(e) => setMonthLocal(parseInt(e.target.value))} size="small">
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleFetch}
                disabled={loading}
                sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: PRIMARY_DARK }, textTransform: "none", borderRadius: 2, px: 3 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Report"}
              </Button>  
              <Button
                variant="outlined"
                startIcon={printLoading ? <CircularProgress size={20} /> : <PrintIcon />}
                onClick={handlePrint}
                disabled={loading || data.length === 0 || printLoading}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {printLoading ? "Printing..." : "Print"}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => {}}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* ========================================================== */}
      {/* ETF R4 PRINT VIEW - EXACT MATCH TO REFERENCE IMAGE */}
      {/* ========================================================== */}
      <div ref={printRef} style={{ width: "100%", maxWidth: "210mm", margin: "0 auto" }}>
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            "@media print": {
              boxShadow: "none",
              p: 2,
            },
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#000000" }}>
              {/* Title Row: Act No. | EMPLOYEES' TRUST FUND BOARD | Advice of remittance form box */}
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                <Box sx={{ flex: "0 0 26%", pt: 0.5 }}>
                  <Typography sx={{ fontSize: "9px", fontWeight: 500 }}>Act No. 46 of 1980</Typography>
                </Box>
                <Box sx={{ flex: "1 1 44%", textAlign: "center", pt: 0.5 }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, lineHeight: 1.3 }}>
                    EMPLOYEES' TRUST FUND
                    <br />
                    BOARD
                  </Typography>
                </Box>
                <Box sx={{ flex: "0 0 30%" }}>
                  <Box sx={{ border: cellBorder, minHeight: "56px", p: 0.75 }}>
                    <Typography sx={{ fontSize: "9px", fontWeight: 500 }}>
                      Advice of remittance form.{" "}
                      <Box component="span" sx={{ color: "#D32F2F", fontWeight: 700 }}>
                        R4
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Employer Address (left) + Registration/Amount table (right) */}
              <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                {/* Left - Employer Address */}
                <Box sx={{ flex: "0 0 58%" }}>
                  <Typography sx={{ fontSize: "9px", fontWeight: 500, mb: 0.25 }}>Employer's Address</Typography>
                  <Box
                    sx={{
                      border: cellBorder,
                      minHeight: "150px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ p: 1, fontSize: "9px" }}>
                      {employerName || "AMSK Constructions (Pvt) Ltd."} {employerAddress || "1/29 New Town Madampe, Sri Lanka"}
                    </Box>
                    <Box sx={{ borderTop: cellBorder, textAlign: "center", py: 0.5 }}>
                      <Typography sx={{ fontSize: "9px", fontWeight: 500 }}>
                        FOR THE USE OF CONTRIBUTORS WITH LESS THAN 15 EMPLOYEES
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Right - Registration / Month / No. of Employees + Amounts */}
                <Box sx={{ flex: "1 1 42%" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8px", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "50%" }} />
                      <col style={{ width: "25%" }} />
                      <col style={{ width: "25%" }} />
                    </colgroup>
                    <tbody>
                      <tr>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "7px", fontWeight: 500 }}>
                          EPF/PPF Registration
                          <br />
                          Number
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "7px", fontWeight: 500 }}>
                          Year &amp; Month of
                          <br />
                          Contribution
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "7px", fontWeight: 500 }}>
                          No. of
                          <br />
                          Employees
                        </td>
                      </tr>
                      <tr>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "10px", fontWeight: 700 }}>
                          {epfRegistrationNumber || "P6912"}
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "10px", fontWeight: 700 }}>
                          {getMonthShort(selectedMonth || month)}-{selectedYear || year}
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "10px", fontWeight: 700 }}>
                          {noOfEmployees}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "8px", fontWeight: 500 }}>
                          Amount of Contribution
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "9px", fontWeight: 700 }}>
                          {formatCurrency(totalContribution)}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "8px", fontWeight: 500 }}>
                          Surcharge
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "9px" }}></td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "8px", fontWeight: 500 }}>
                          Total Amount of Remittance
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "9px" }}></td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "8px", fontWeight: 500 }}>
                          Cheque No.
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "9px" }}></td>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "8px", fontWeight: 500 }}>
                          Bank &amp; Branch
                        </td>
                        <td style={{ border: cellBorder, padding: "3px 4px", textAlign: "center", fontSize: "9px" }}></td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Instructions */}
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 500 }}>
                  Please refer the instructions stated overleaf
                </Typography>
              </Box>

              {/* Data Table */}
              {!loading && data.length > 0 ? (
                <>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8px" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", fontWeight: 700, fontSize: "8px", padding: "3px 4px", border: cellBorder, width: "35%" }}>
                          Employee's Name
                        </th>
                        <th style={{ textAlign: "left", fontWeight: 700, fontSize: "8px", padding: "3px 4px", border: cellBorder, width: "25%" }}>
                          National Identity Card No.
                        </th>
                        <th style={{ textAlign: "center", fontWeight: 700, fontSize: "8px", padding: "3px 4px", border: cellBorder, width: "15%" }}>
                          Member No.
                        </th>
                        <th style={{ textAlign: "center", fontWeight: 700, fontSize: "8px", padding: "3px 4px", border: cellBorder, width: "15%" }}>
                          Contribution
                        </th>
                        <th style={{ textAlign: "center", fontWeight: 700, fontSize: "8px", padding: "3px 4px", border: cellBorder, width: "10%" }}>
                          Cents
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, index) => {
                        const contribution = splitAmount(item.Contribution);
                        return (
                          <tr key={index}>
                            <td style={{ fontSize: "8px", padding: "2px 4px", border: cellBorder, whiteSpace: "nowrap" }}>
                              {item.EmployeeName || ""}
                            </td>
                            <td style={{ fontSize: "8px", padding: "2px 4px", border: cellBorder, whiteSpace: "nowrap" }}>
                              {item.NationalIdentityCardNo || ""}
                            </td>
                            <td style={{ textAlign: "center", fontSize: "8px", padding: "2px 4px", border: cellBorder }}>
                              {item.MemberNo || ""}
                            </td>
                            <td style={{ textAlign: "right", fontSize: "8px", padding: "2px 4px", border: cellBorder }}>
                              {contribution.rupees}
                            </td>
                            <td style={{ textAlign: "center", fontSize: "8px", padding: "2px 4px", border: cellBorder }}>
                              {contribution.cents}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Total Row */}
                      <tr>
                        <td colSpan={3} style={{ border: "none", padding: "4px" }}></td>
                        <td style={{ fontWeight: 700, textAlign: "right", borderTop: "1px solid #000", borderBottom: cellBorder, fontSize: "8px", padding: "2px 4px" }}>
                          {totalSplit.rupees}
                        </td>
                        <td style={{ fontWeight: 700, textAlign: "center", borderTop: "1px solid #000", borderBottom: cellBorder, fontSize: "8px", padding: "2px 4px" }}>
                          {totalSplit.cents}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Certification Section */}
                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
                      I certify that the information given above is correct.
                    </Typography>
                    <Typography sx={{ fontSize: "10px", fontWeight: 400, mt: 1 }}>
                      Signature of Employer.....................................
                    </Typography>
                    <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
                        Telephone No.........................
                      </Typography>
                      <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
                        E-mail Address .....................................
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "10px", fontWeight: 400, mt: 1 }}>
                      Date.........................
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No data available. Please generate the report.
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </div>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} AMSK Constructions (Pvt) Ltd. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default KFormPage;